import { FilterOutlined } from '@ant-design/icons'
import { Input } from 'antd'
import { fuzzyMatch } from 'features/libra/helpers/fuzzyMatch'
import { type FC, useMemo, useState } from 'react'
import styled from 'styled-components'

import type { LibraDashboardConfig } from './libraDashboardApi'

type LibraDashboardSelectProps = {
  configs: ReadonlyArray<LibraDashboardConfig>
  selectedConfigId?: string
  setSelectedConfigId: (configId: string) => void
}

export const LibraDashboardSelect: FC<LibraDashboardSelectProps> = ({
  configs,
  selectedConfigId,
  setSelectedConfigId,
}) => {
  const [filterText, setFilterText] = useState('')
  const filteredConfigs = useMemo(() => {
    const query = filterText.trim().toLowerCase()
    if (!query) return configs
    return configs.filter((config) => fuzzyMatch(config.title, query))
  }, [configs, filterText])

  return (
    <ConfigList>
      <FilterContainer>
        <Input
          placeholder='Filter dashboards'
          value={filterText}
          onChange={(event) => setFilterText(event.target.value)}
          allowClear
          prefix={<FilterIcon />}
        />
      </FilterContainer>
      {filteredConfigs.map((config) => (
        <ConfigButton
          key={config.id}
          type='button'
          $isSelected={config.id === selectedConfigId}
          onClick={() => setSelectedConfigId(config.id)}>
          {config.title}
        </ConfigButton>
      ))}
    </ConfigList>
  )
}

const ConfigList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 12rem;
`

const FilterContainer = styled.div`
  padding-bottom: 0.25rem;
`

const FilterIcon = styled(FilterOutlined)`
  color: ${({ theme }) => theme.colors.gray[700]};
`

const ConfigButton = styled.button<{ $isSelected: boolean }>`
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.forecast.border};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.background3 : 'transparent'};
  color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
  }
`
